import { useStreamDailyEvents, useStreams } from '@bloo-team/core-shell'
import type { MaturitySignal, SignalReading } from '@bloo-team/core-shell'
import { useExtractors } from '../lib/extractors'
import { useSources } from '../lib/sources'
import { useDataModel } from '../lib/data-model'
import { useStreamConfig } from '../lib/retention'
import {
  extractorsEnabled,
  extractorsHealthy,
  ingestToday,
  retentionSet,
  sourcesReporting,
  sourcesStale,
  storageGoverned,
  streamsLive,
  streamsModelled,
} from './signal-data'

/**
 * Datafabric's deployment-journey signals — phases Connect and Understand
 * (`docs/home-journey-requirements.md` §5, S3).
 *
 * Each `read` is a hook, and a thin one: it calls the SAME hooks the fabric
 * pages call — same query keys, one cache — and hands what came back to the
 * pure decision in `signal-data.ts`. Nothing here introduces a read the
 * product's own pages do not already make, which is what keeps Home from
 * disagreeing with the surface it links to (`docs/home.md`).
 */

/** The page the Overview also reads, so both count over the same devices. */
const OVERVIEW_PAGE = 1000

/**
 * What a signal reads while its queries are in flight or after they failed.
 * Never a value: a zero standing in for a read that did not land says "you
 * have not done this", which is a different and wrong statement (J7).
 */
function pending(reads: Array<{ isPending: boolean; isError: boolean }>): SignalReading | null {
  if (reads.some((r) => r.isError)) return { state: 'error', value: null }
  if (reads.some((r) => r.isPending)) return { state: 'loading', value: null }
  return null
}

/**
 * "Now" is the READ's time (`dataUpdatedAt`), never `Date.now()` in render —
 * the rule the home widgets already follow (`docs/home.md`), and one the
 * purity lint enforces: a clock read during render makes an age that changes
 * whenever the component happens to re-render. A read with no stamp has not
 * landed, whatever its flags say, so it reads as loading.
 */
function readAt(...reads: Array<{ dataUpdatedAt: number }>): number | null {
  const stamps = reads.map((r) => r.dataUpdatedAt).filter((t) => t > 0)
  return stamps.length === reads.length ? Math.max(...stamps) : null
}

const LOADING: SignalReading = { state: 'loading', value: null }

type Scope = { clusterId: string; scopeId: string }

function useSourcesReporting({ clusterId, scopeId }: Scope): SignalReading {
  const sources = useSources({ clusterId, scopeId, pageNo: 1, pageSize: OVERVIEW_PAGE })
  const blocked = pending([sources])
  const now = readAt(sources)
  if (blocked) return blocked
  if (now === null) return LOADING
  return sourcesReporting(sources.data?.devices ?? [], sources.data?.total ?? 0, now)
}

function useSourcesStale({ clusterId, scopeId }: Scope): SignalReading {
  const sources = useSources({ clusterId, scopeId, pageNo: 1, pageSize: OVERVIEW_PAGE })
  const blocked = pending([sources])
  const now = readAt(sources)
  if (blocked) return blocked
  if (now === null) return LOADING
  return sourcesStale(sources.data?.devices ?? [], now)
}

function useStreamsLive({ clusterId, scopeId }: Scope): SignalReading {
  const streams = useStreams({ clusterId, scopeId })
  const daily = useStreamDailyEvents({ clusterId, scopeId })
  const blocked = pending([streams, daily])
  const now = readAt(streams, daily)
  if (blocked) return blocked
  if (now === null) return LOADING
  return streamsLive(streams.data?.streams ?? [], daily.data ?? {}, now)
}

function useIngestToday({ clusterId, scopeId }: Scope): SignalReading {
  const daily = useStreamDailyEvents({ clusterId, scopeId })
  return pending([daily]) ?? ingestToday(daily.data ?? {})
}

function useExtractorsEnabled({ clusterId, scopeId }: Scope): SignalReading {
  const extractors = useExtractors({ clusterId, scopeId })
  return pending([extractors]) ?? extractorsEnabled(extractors.data?.extractors ?? [])
}

function useExtractorsHealthy({ clusterId, scopeId }: Scope): SignalReading {
  const extractors = useExtractors({ clusterId, scopeId })
  return pending([extractors]) ?? extractorsHealthy(extractors.data?.extractors ?? [])
}

function useStreamsModelled({ clusterId, scopeId }: Scope): SignalReading {
  const streams = useStreams({ clusterId, scopeId })
  const ddm = useDataModel({ clusterId, scopeId })
  return pending([streams, ddm]) ?? streamsModelled(streams.data?.streams ?? [], ddm.data ?? [])
}

function useRetentionSet({ clusterId, scopeId }: Scope): SignalReading {
  const config = useStreamConfig({ clusterId, scopeId })
  return pending([config]) ?? retentionSet(config.cluster, config.scope)
}

function useStorageGoverned({ clusterId, scopeId }: Scope): SignalReading {
  const streams = useStreams({ clusterId, scopeId })
  return pending([streams]) ?? storageGoverned(streams.data?.streams ?? [])
}

/**
 * The contributions, in the order the board reads them.
 *
 * Weights: 3 where the phase means nothing without it (a scope with no
 * sources is not "80% connected"; unparsed events cannot be understood), 1
 * where it is a good habit rather than a gate.
 */
export const fabricMaturitySignals: MaturitySignal[] = [
  { id: 'fabric.sources-reporting', phase: 'connect', title: 'Sources reporting', weight: 3, read: useSourcesReporting },
  { id: 'fabric.streams-live', phase: 'connect', title: 'Streams carrying events', weight: 3, read: useStreamsLive },
  { id: 'fabric.ingest-today', phase: 'connect', title: 'Ingest today', weight: 2, read: useIngestToday },
  { id: 'fabric.sources-stale', phase: 'connect', title: 'Nothing gone quiet', weight: 2, read: useSourcesStale },
  {
    id: 'fabric.extractors-enabled',
    phase: 'understand',
    title: 'Extractors running',
    weight: 3,
    read: useExtractorsEnabled,
  },
  {
    id: 'fabric.extractors-healthy',
    phase: 'understand',
    title: 'Extractors healthy',
    weight: 2,
    read: useExtractorsHealthy,
  },
  { id: 'fabric.streams-modelled', phase: 'understand', title: 'Streams modelled', weight: 2, read: useStreamsModelled },
  { id: 'fabric.retention-set', phase: 'understand', title: 'Retention set', weight: 2, read: useRetentionSet },
  { id: 'fabric.storage-governed', phase: 'understand', title: 'Storage governed', weight: 1, read: useStorageGoverned },
]
